import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoClose, IoCameraOutline } from "react-icons/io5";
import { FiUpload, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import CInput from "../../../../../components/CInput";
import CAutocomplete from "../../../../../components/CAutocomplete";
import {
  createIssueLogWithImage,
  getIssueCategoryActivityTypes,
  getIssueCategoriesByActivityType,
  listProjectRooms,
} from "../../../../../services/issueLogService";
import { SOURCE_TYPE, SEVERITY } from "../../ProjectLogIssues/constants";
import IssueLogAnnotationCanvas from "../../ProjectLogIssues/IssueLogAnnotationCanvas";

/* ---------- severity pills (readonly, mirrors portal PRIORITY_PILLS) ---------- */
const SEVERITY_PILLS = [
  { value: SEVERITY.CRITICAL, label: "Critical", bg: "rgba(239, 68, 68, 0.1)", color: "#dc2626", dot: "#dc2626" },
  { value: SEVERITY.HIGH, label: "High", bg: "rgba(249, 115, 22, 0.1)", color: "#ea580c", dot: "#ea580c" },
  { value: SEVERITY.MEDIUM, label: "Medium", bg: "rgba(245, 158, 11, 0.1)", color: "#d97706", dot: "#d97706" },
  { value: SEVERITY.LOW, label: "Low", bg: "rgba(34, 197, 94, 0.1)", color: "#16a34a", dot: "#16a34a" },
];

const SeverityPill = ({ severity }) => {
  const sev = (severity || "").toUpperCase();
  const match = SEVERITY_PILLS.find((p) => p.value === sev);
  if (!match) return <span style={{ fontSize: 13, color: "#6b7280" }}>—</span>;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 9999,
        fontSize: 12,
        fontWeight: 500,
        background: match.bg,
        color: match.color,
        border: `1px solid ${match.color}22`,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 9999,
          background: match.dot,
          display: "inline-block",
        }}
      />
      {match.label}
    </span>
  );
};

/* ---------- dataURL → File helper ---------- */
const dataURLtoFile = (dataURL, filename) => {
  const [header, base64] = dataURL.split(",");
  const mime = /data:(.*?);/.exec(header)?.[1] || "image/png";
  const bin = atob(base64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return new File([u8], filename, { type: mime });
};

/* ---------- camera overlay (separate fixed-position modal above parent) ---------- */
function CameraOverlay({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (err) {
        setError(err?.message || "Could not access camera");
      }
    })();

    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      cancelled = true;
      window.removeEventListener("keydown", onKey);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    onClose?.();
  };

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    onCapture?.(dataUrl);
  };

  const cornerStyle = (corner) => {
    const common = {
      position: "absolute",
      width: 28,
      height: 28,
      borderColor: "#fff",
      borderStyle: "solid",
    };
    if (corner === "tl") return { ...common, top: 16, left: 16, borderWidth: "3px 0 0 3px" };
    if (corner === "tr") return { ...common, top: 16, right: 16, borderWidth: "3px 3px 0 0" };
    if (corner === "bl") return { ...common, bottom: 16, left: 16, borderWidth: "0 0 3px 3px" };
    return { ...common, bottom: 16, right: 16, borderWidth: "0 3px 3px 0" };
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.7)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 12000,
        padding: 16,
      }}
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 760,
          background: "#0d0d0d",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={handleClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 2,
            width: 36,
            height: 36,
            borderRadius: 9999,
            border: "none",
            background: "rgba(0,0,0,0.5)",
            color: "#fff",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label="Close camera"
        >
          <IoClose size={20} />
        </button>

        {error ? (
          <div style={{ padding: 32, color: "#fca5a5", textAlign: "center" }}>
            {error}
          </div>
        ) : (
          <div style={{ position: "relative", aspectRatio: "4 / 3", background: "#000" }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <span style={cornerStyle("tl")} aria-hidden />
            <span style={cornerStyle("tr")} aria-hidden />
            <span style={cornerStyle("bl")} aria-hidden />
            <span style={cornerStyle("br")} aria-hidden />
          </div>
        )}

        <canvas ref={canvasRef} style={{ display: "none" }} />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 20,
            background: "#0d0d0d",
          }}
        >
          <button
            type="button"
            onClick={capture}
            disabled={!!error}
            style={{
              width: 64,
              height: 64,
              borderRadius: 9999,
              border: "4px solid #fff",
              background: "#fff",
              cursor: error ? "not-allowed" : "pointer",
              boxShadow: "0 0 0 4px rgba(255,255,255,0.2)",
              opacity: error ? 0.5 : 1,
            }}
            aria-label="Capture"
          />
        </div>
      </div>
    </div>
  );
}

/* ---------- main modal ---------- */
export default function AddIssueLogModal({
  projectId,
  updateId,
  activities = [],
  // When true, renders a plain text input for room name instead of the
  // paginated rooms autocomplete and skips the rooms fetch. The text
  // value goes to the backend as `room_name` only (no `room_id`).
  roomAsText = false,
  // Override the source_type sent on create. Defaults to DAILY_UPDATE
  // (existing behavior). Contractor side uses CONTRACTOR.
  sourceType: sourceTypeProp,
  // When true, the create payload omits the `activity` object and the
  // activity-required validation is skipped.
  omitActivity = false,
  onClose,
  onSaved,
}) {
  const [step, setStep] = useState(1);

  /* dropdowns */
  const [activityTypes, setActivityTypes] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [issueTypesLoading, setIssueTypesLoading] = useState(false);

  /* room infinite scroll */
  const [rooms, setRooms] = useState([]);
  const [roomSearch, setRoomSearch] = useState("");
  const [roomPage, setRoomPage] = useState(1);
  const [roomHasNext, setRoomHasNext] = useState(false);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomLoadingMore, setRoomLoadingMore] = useState(false);
  const roomReqId = useRef(0);

  /* form values */
  const [roomId, setRoomId] = useState("");
  const [roomNameText, setRoomNameText] = useState("");
  const [activityId, setActivityId] = useState("");
  const [activityType, setActivityType] = useState("");
  const [issueTypeId, setIssueTypeId] = useState("");
  const [description, setDescription] = useState("");

  /* image state */
  const [rawImage, setRawImage] = useState(null);
  const [annotatedImage, setAnnotatedImage] = useState(null);
  const [annotationMeta, setAnnotationMeta] = useState(null);

  /* overlays */
  const [showCamera, setShowCamera] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  /* ---------- activity types (once) ---------- */
  useEffect(() => {
    let cancelled = false;
    getIssueCategoryActivityTypes()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setActivityTypes(list);
      })
      .catch((err) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------- issue types whenever activityType changes ---------- */
  useEffect(() => {
    setIssueTypeId("");
    if (!activityType) {
      setIssueTypes([]);
      return;
    }
    let cancelled = false;
    setIssueTypesLoading(true);
    getIssueCategoriesByActivityType(activityType)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setIssueTypes(list);
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (!cancelled) setIssueTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activityType]);

  /* ---------- rooms (page 1 on mount + on search change ≥3 chars) ---------- */
  const fetchRoomsPage = useCallback(
    async (page, search, { append }) => {
      if (!projectId || roomAsText) return;
      const myReq = ++roomReqId.current;
      if (append) setRoomLoadingMore(true);
      else setRoomsLoading(true);
      try {
        const res = await listProjectRooms(projectId, { page, search });
        if (myReq !== roomReqId.current) return;
        const items = res?.data || [];
        setRooms((prev) => (append ? [...prev, ...items] : items));
        setRoomPage(res?.page || page);
        setRoomHasNext(!!res?.next_enable);
      } catch (err) {
        console.error("rooms fetch failed", err);
      } finally {
        if (myReq !== roomReqId.current) return;
        setRoomsLoading(false);
        setRoomLoadingMore(false);
      }
    },
    [projectId, roomAsText]
  );

  useEffect(() => {
    const q = roomSearch.trim();
    if (q.length > 0 && q.length < 3) {
      // don't hit API for 1-2 chars; keep previous rooms visible
      return;
    }
    const handle = setTimeout(() => {
      fetchRoomsPage(1, q, { append: false });
    }, q.length >= 3 ? 300 : 0);
    return () => clearTimeout(handle);
  }, [roomSearch, fetchRoomsPage]);

  const loadMoreRooms = () => {
    if (!roomHasNext || roomLoadingMore || roomsLoading) return;
    fetchRoomsPage(roomPage + 1, roomSearch.trim(), { append: true });
  };

  const handleRoomListScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      loadMoreRooms();
    }
  };

  /* ---------- derived ---------- */
  const selectedIssueType = issueTypes.find(
    (t) => String(t?.id) === String(issueTypeId)
  );
  const selectedCategoryName = selectedIssueType?.category || "";
  const severityFromCategory = selectedIssueType?.severity || "";

  const selectedRoom = rooms.find(
    (r) => String(r.room_id || r.id) === String(roomId)
  );

  /* ---------- photo ---------- */
  const handleFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setRawImage(reader.result);
      setAnnotatedImage(null);
      setAnnotationMeta(null);
      setStep(2);
    };
    reader.readAsDataURL(f);
  };

  const handleCameraCapture = (dataUrl) => {
    setShowCamera(false);
    setRawImage(dataUrl);
    setAnnotatedImage(null);
    setAnnotationMeta(null);
    setStep(2);
  };

  const removeImage = () => {
    setRawImage(null);
    setAnnotatedImage(null);
    setAnnotationMeta(null);
  };

  /* ---------- annotation confirm → back to step 1 ---------- */
  const handleAnnotationConfirm = (dataUrl, meta) => {
    setAnnotatedImage(dataUrl);
    setAnnotationMeta(meta || { coordinates: [], markup: 0 });
    setStep(1);
  };

  /* ---------- submit ---------- */
  const validate = () => {
    if (!description.trim()) {
      toast.error("Description is required");
      return false;
    }
    if (!omitActivity && !activityId) {
      toast.error("Activity is required");
      return false;
    }
    if (!activityType) {
      toast.error("Activity Type is required");
      return false;
    }
    if (!selectedIssueType) {
      toast.error("Issue Type is required");
      return false;
    }
    if (!annotatedImage) {
      toast.error("Please add and annotate a photo");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const file = dataURLtoFile(annotatedImage, `issue-log-${Date.now()}.png`);
    const meta = annotationMeta ?? { coordinates: [], markup: 0 };

    // Activity options may carry a `milestone_id` distinct from `id` (e.g.
    // contractor-side options where `id` is a composite "<milestoneId>-<idx>"
    // for unique deliverable rows). Fall back to the option's id (legacy
    // shape where the id IS the milestoneId).
    const selectedActivity =
      activities.find((a) => String(a.id) === String(activityId)) || null;
    const milestoneIdForPayload = Number(
      selectedActivity?.milestone_id ?? activityId
    );

    const trimmedRoomName = roomNameText.trim();
    const roomFields = roomAsText
      ? trimmedRoomName
        ? { room_name: trimmedRoomName }
        : {}
      : selectedRoom != null
      ? {
          room_id: Number(selectedRoom.room_id || selectedRoom.id),
          room_name: String(selectedRoom.room_name || selectedRoom.name || ""),
        }
      : {};

    const activityFields = omitActivity
      ? {}
      : { activity: { milestoneId: milestoneIdForPayload } };

    const payload = {
      description: description.trim().slice(0, 1000),
      metadata: JSON.stringify(meta),
      markup: meta.markup ?? 0,
      project_id: Number(projectId),
      source_id: updateId != null ? Number(updateId) : 0,
      source_type: sourceTypeProp || SOURCE_TYPE.DAILY_UPDATE,
      ...activityFields,
      issue_category: selectedIssueType,
      ...roomFields,
    };

    setSubmitting(true);
    try {
      const res = await createIssueLogWithImage({ image: file, issueLog: payload });
      if (res?.id || res?.success) {
        toast.success("Issue logged");
        onSaved?.();
      } else {
        toast.error(res?.message || "Could not log issue");
      }
    } catch (err) {
      toast.error(err?.message || "Could not log issue");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- render ---------- */
  const roomOptionLabel = (opt) =>
    opt?.room_name || opt?.name || (opt?.id != null ? `Room ${opt.id}` : "");
  const activityOptionLabel = (opt) =>
    opt?.milestone_name || opt?.activity_name || opt?.name || "";

  return (
    <>
      <div className="pud-modal-overlay" onClick={onClose}>
        <div
          className="pud-modal"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: 720, width: "95vw" }}
        >
          <div className="pud-modal-header">
            <div className="pud-modal-title">
              {step === 1 ? "Log Issue" : "Annotate Image"}
            </div>
            <button type="button" className="pud-modal-close" onClick={onClose}>
              <IoClose />
            </button>
          </div>

          {step === 1 && (
            <>
              {/* Room + Activity + Activity Type (one row) */}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: 200 }}>
                  {roomAsText ? (
                    <CInput
                      label="Room"
                      placeholder="Enter room name"
                      value={roomNameText}
                      onChange={(e) =>
                        setRoomNameText((e.target.value || "").slice(0, 120))
                      }
                    />
                  ) : (
                    <CAutocomplete
                      label="Room"
                      placeholder={
                        roomsLoading
                          ? "Loading rooms..."
                          : roomSearch && roomSearch.trim().length < 3
                          ? "Type 3+ characters"
                          : rooms.length === 0
                          ? "No rooms found"
                          : "Select a room"
                      }
                      value={selectedRoom || null}
                      onChange={(_e, v) =>
                        setRoomId(v ? String(v.room_id || v.id) : "")
                      }
                      onInputChange={(_e, input, reason) => {
                        if (reason === "input") setRoomSearch(input);
                        if (reason === "clear") setRoomSearch("");
                      }}
                      options={rooms}
                      loading={roomsLoading || roomLoadingMore}
                      filterOptions={(opts) => opts}
                      getOptionLabel={roomOptionLabel}
                      isOptionEqualToValue={(a, b) =>
                        String(a?.room_id || a?.id) ===
                        String(b?.room_id || b?.id)
                      }
                      ListboxProps={{ onScroll: handleRoomListScroll }}
                    />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <CAutocomplete
                    label="Activity"
                    placeholder={
                      activities.length === 0
                        ? "No activities"
                        : "Select activity"
                    }
                    value={
                      activities.find(
                        (a) => String(a.id) === String(activityId)
                      ) || null
                    }
                    onChange={(_e, v) => setActivityId(v ? String(v.id) : "")}
                    options={activities || []}
                    getOptionLabel={activityOptionLabel}
                    isOptionEqualToValue={(a, b) =>
                      String(a?.id) === String(b?.id)
                    }
                    disabled={!activities || activities.length === 0}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <CAutocomplete
                    label="Activity Type"
                    placeholder="Select type"
                    value={activityType || null}
                    onChange={(_e, v) => setActivityType(v || "")}
                    options={activityTypes}
                    getOptionLabel={(o) => o || ""}
                    isOptionEqualToValue={(a, b) => a === b}
                  />
                </div>
              </div>

              {/* Issue Type */}
              <div style={{ marginBottom: 12 }}>
                <CAutocomplete
                  label="Issue Type"
                  placeholder={
                    !activityType
                      ? "Pick activity type first"
                      : issueTypesLoading
                      ? "Loading..."
                      : "Select issue type"
                  }
                  value={selectedIssueType || null}
                  onChange={(_e, v) => setIssueTypeId(v ? String(v.id) : "")}
                  options={issueTypes}
                  loading={issueTypesLoading}
                  getOptionLabel={(o) =>
                    o?.description || o?.category || ""
                  }
                  isOptionEqualToValue={(a, b) =>
                    String(a?.id) === String(b?.id)
                  }
                  disabled={!activityType || issueTypesLoading}
                />
              </div>

              {/* Read-only Issue Category + Severity (shown when an issueType is picked) */}
              {selectedIssueType && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#0d0d0e",
                        marginBottom: 6,
                      }}
                    >
                      Issue Category
                    </div>
                    <div
                      style={{
                        padding: "10px 14px",
                        borderRadius: 10,
                        border: "1px solid rgba(18, 18, 21, 0.10)",
                        background: "#fff",
                        color: "#0d0d0e",
                        fontSize: 14,
                        fontFamily: "Inter Tight",
                        cursor: "not-allowed",
                      }}
                    >
                      {selectedCategoryName || "—"}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#0d0d0e",
                        marginBottom: 6,
                      }}
                    >
                      Severity
                    </div>
                    <SeverityPill severity={severityFromCategory} />
                  </div>
                </>
              )}

             

              {/* Photo Evidence */}
              <div className="pud-field">
                <label className="pud-label">Photo Evidence</label>
                {annotatedImage ? (
                  <div
                    style={{
                      position: "relative",
                      width: 180,
                      height: 140,
                      borderRadius: 10,
                      overflow: "hidden",
                      border: "1px solid #e5e7eb",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={annotatedImage}
                      alt="Annotated"
                      onClick={() => setLightbox(true)}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        width: 24,
                        height: 24,
                        borderRadius: 9999,
                        border: "2px solid #fff",
                        background: "#ef4444",
                        color: "#fff",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      aria-label="Remove image"
                    >
                      <FiX size={12} />
                    </button>
                    <span
                      style={{
                        position: "absolute",
                        left: 6,
                        bottom: 6,
                        padding: "3px 8px",
                        borderRadius: 9999,
                        background: "rgba(13,13,14,0.75)",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 500,
                      }}
                    >
                      Annotated
                    </span>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "10px 16px",
                        borderRadius: 10,
                        border: "1px solid #4D6BF6",
                        background: "#fff",
                        color: "#0d0d0e",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      <FiUpload size={16} />
                      Upload File
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFile}
                        style={{ display: "none" }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCamera(true)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "10px 16px",
                        borderRadius: 10,
                        border: "1px solid #4D6BF6",
                        background: "#fff",
                        color: "#0d0d0e",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      <IoCameraOutline size={16} />
                      Open Camera
                    </button>
                  </div>
                )}
              </div>

               {/* Description */}
              <div style={{ marginBottom: 12 }}>
                <CInput
                  multiline
                  rows={4}
                  label="Description"
                  placeholder="Describe the issue..."
                  value={description}
                  onChange={(e) =>
                    setDescription((e.target.value || "").slice(0, 1000))
                  }
                />
                <div
                  style={{
                    fontSize: 11,
                    color: "#949daa",
                    textAlign: "right",
                    marginTop: 4,
                  }}
                >
                  {description.length} / 1000
                </div>
              </div>

              {/* Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 16,
                }}
              >
                <button
                  type="button"
                  className="pud-btn"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pud-btn pud-btn-primary"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Logging..." : "Log Issue"}
                </button>
              </div>
            </>
          )}

          {step === 2 && rawImage && (
            <div style={{ display: "flex", flexDirection: "column", height: "70vh" }}>
              <IssueLogAnnotationCanvas
                imageSrc={rawImage}
                onBack={() => setStep(1)}
                onConfirm={handleAnnotationConfirm}
              />
            </div>
          )}
        </div>
      </div>

      {showCamera && (
        <CameraOverlay
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}

      {lightbox && annotatedImage && (
        <div
          onClick={() => setLightbox(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 12500,
            padding: 24,
            cursor: "zoom-out",
          }}
        >
          <img
            src={annotatedImage}
            alt="Annotated preview"
            style={{
              maxWidth: "95vw",
              maxHeight: "90vh",
              borderRadius: 8,
              boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
            }}
          />
        </div>
      )}
    </>
  );
}
